import { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Bot, Send, Wifi, WifiOff, MessageSquare, Terminal, BookOpen, ArrowRight, Link2, Unlink, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function MoltBotHelpPage() {
  const { user } = useAuth();
  const [botStatus, setBotStatus] = useState(null);
  const [command, setCommand] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [pairingStatus, setPairingStatus] = useState(null);
  const [pairingCode, setPairingCode] = useState('');
  const [pairing, setPairing] = useState(false);

  useEffect(() => { loadStatus(); loadPairingStatus(); }, []);

  const loadStatus = async () => {
    try {
      const { data } = await api.get('/bot/status');
      setBotStatus(data);
    } catch (err) { /* ignore */ }
  };

  const loadPairingStatus = async () => {
    try {
      const { data } = await api.get('/channels/telegram/pairing-status');
      setPairingStatus(data);
    } catch (err) { /* ignore */ }
  };

  const handlePair = async () => {
    if (!pairingCode.trim() || pairingCode.length !== 6) {
      toast.error('Enter a 6-digit pairing code');
      return;
    }
    setPairing(true);
    try {
      const { data } = await api.post(`/channels/telegram/pair?code=${pairingCode}`);
      toast.success(data.message);
      setPairingCode('');
      loadPairingStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Pairing failed');
    } finally { setPairing(false); }
  };

  const handleUnlink = async () => {
    if (!window.confirm('Unlink your Telegram account?')) return;
    try {
      await api.delete('/channels/telegram/unlink');
      toast.success('Telegram unlinked');
      setPairingStatus({ linked: false });
    } catch (err) { toast.error('Failed to unlink'); }
  };

  const sendCommand = async (e) => {
    e.preventDefault();
    if (!command.trim()) return;
    const userMsg = command.trim();
    setChatHistory(prev => [...prev, { role: 'user', text: userMsg }]);
    setCommand('');
    setSending(true);
    try {
      const { data } = await api.post('/bot/command', { text: userMsg });
      setChatHistory(prev => [...prev, { role: 'bot', text: data.message, success: data.success }]);
    } catch (err) {
      setChatHistory(prev => [...prev, { role: 'bot', text: err.response?.data?.detail || 'Error processing command', success: false }]);
    } finally { setSending(false); }
  };

  return (
    <div data-testid="moltbot-page" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="font-barlow font-bold text-3xl text-foreground tracking-tight flex items-center gap-3">
          <Bot className="w-8 h-8 text-orange-500" /> MoltBot
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Maintenance operations assistant</p>
      </div>

      {/* Status */}
      <Card className="bg-card border-border shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${botStatus?.online ? 'bg-emerald-50 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
              {botStatus?.online ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">
                MoltBot is {botStatus?.online ? 'online' : 'offline'}
              </p>
              <p className="text-xs text-muted-foreground">
                Service account: {botStatus?.user || 'Not configured'}
              </p>
            </div>
            <div className="ms-auto flex gap-4">
              <div className="text-center">
                <div className={`w-2 h-2 rounded-full mx-auto mb-1 ${botStatus?.channels?.telegram?.connected ? 'bg-emerald-500' : 'bg-zinc-300'}`} />
                <span className="text-xs text-muted-foreground">Telegram</span>
              </div>
              <div className="text-center">
                <div className={`w-2 h-2 rounded-full mx-auto mb-1 ${botStatus?.channels?.whatsapp?.connected ? 'bg-emerald-500' : 'bg-zinc-300'}`} />
                <span className="text-xs text-muted-foreground">WhatsApp</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Telegram Linking */}
      <Card className="bg-card border-border shadow-sm" data-testid="telegram-pairing-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
            <Link2 className="w-4 h-4" /> Link Telegram Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pairingStatus?.linked ? (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Linked to Telegram as <strong>{pairingStatus.telegram_name || 'User'}</strong>
                  </p>
                  <p className="text-xs text-muted-foreground">You can now use commands in Telegram</p>
                </div>
              </div>
              <Button size="sm" variant="outline" className="ms-auto text-rose-600 border-rose-200 hover:bg-rose-50" onClick={handleUnlink} data-testid="unlink-telegram-btn">
                <Unlink className="w-3.5 h-3.5 me-1" /> Unlink
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                To link your Telegram, open the MoltBot in Telegram and send <code className="bg-muted px-1.5 py-0.5 rounded text-xs">/pair</code>. Enter the 6-digit code below.
              </p>
              <div className="flex gap-2 items-end">
                <div className="space-y-1 flex-1 max-w-[200px]">
                  <Label className="text-xs text-muted-foreground">Pairing Code</Label>
                  <Input
                    value={pairingCode}
                    onChange={e => setPairingCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="font-mono text-center tracking-widest text-lg h-11"
                    data-testid="pairing-code-input"
                  />
                </div>
                <Button onClick={handlePair} disabled={pairing || pairingCode.length !== 6} className="bg-brand hover:bg-brand-hover text-brand-foreground h-11" data-testid="pair-telegram-btn">
                  <Link2 className="w-4 h-4 me-1.5" /> {pairing ? 'Linking...' : 'Link'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chat Interface */}
      <Card className="bg-card border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
            <Terminal className="w-4 h-4" /> Command Console
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 min-h-[200px] max-h-[400px] overflow-y-auto space-y-3" data-testid="bot-chat-history">
            {chatHistory.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                Send a command to MoltBot. Try: "List open work orders in KAFD"
              </p>
            )}
            {chatHistory.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'bg-zinc-900 text-white'
                    : msg.success === false
                      ? 'bg-rose-50 text-rose-700 border border-rose-200'
                      : 'bg-card text-muted-foreground border-border'
                }`}>
                  <pre className="whitespace-pre-wrap font-sans">{msg.text}</pre>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-card text-muted-foreground border-border rounded-lg px-3 py-2 text-sm">
                  Processing...
                </div>
              </div>
            )}
          </div>
          <form onSubmit={sendCommand} className="flex gap-2" data-testid="bot-command-form">
            <Input
              value={command}
              onChange={e => setCommand(e.target.value)}
              placeholder="Type a command..."
              className="flex-1"
              data-testid="bot-command-input"
            />
            <Button type="submit" disabled={!command.trim() || sending} className="bg-brand hover:bg-brand-hover text-brand-foreground" data-testid="bot-send-btn">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Example Commands */}
      <Card className="bg-card border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Example Commands
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              'Create a maintenance request in KAFD, Room The Vault, lock issue, high priority',
              'List open work orders in KAFD',
              'What is overdue today?',
              'List all work orders',
            ].map((cmd, i) => (
              <button
                key={i}
                className="text-start p-3 rounded-lg border-border hover:border-brand/50 hover:bg-muted/50 transition-colors text-sm text-muted-foreground"
                onClick={() => setCommand(cmd)}
                data-testid={`bot-example-${i}`}
              >
                <ArrowRight className="w-3 h-3 inline me-2 text-orange-500" />
                {cmd}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      <Card className="bg-card border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> Channel Setup Instructions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Telegram */}
          <div>
            <h3 className="font-barlow font-semibold text-base text-foreground mb-2">Telegram Setup</h3>
            <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
              <li>Open Telegram and search for <strong>@BotFather</strong></li>
              <li>Send <code className="bg-muted px-1 rounded">/newbot</code> and follow the prompts to create your bot</li>
              <li>Copy the <strong>API token</strong> provided by BotFather</li>
              <li>Provide the token in the MoltBot configuration settings</li>
              <li>Add the bot to your maintenance team group chat</li>
              <li>MoltBot will listen for commands and send notifications to this group</li>
            </ol>
          </div>

          <Separator />

          {/* WhatsApp */}
          <div>
            <h3 className="font-barlow font-semibold text-base text-foreground mb-2">WhatsApp Setup</h3>
            <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
              <li>Set up a <strong>WhatsApp Business API</strong> account via Meta Business Suite</li>
              <li>Configure the webhook URL to point to your MoltBot endpoint</li>
              <li>Scan the QR code with the dedicated phone number</li>
              <li>Add the phone number to your maintenance team contacts</li>
              <li>MoltBot will process messages sent to this number</li>
            </ol>
          </div>

          <Separator />

          {/* Scheduled Tasks */}
          <div>
            <h3 className="font-barlow font-semibold text-base text-foreground mb-2">Scheduled Automations (after Publishing)</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded mt-0.5 flex-shrink-0">09:00 AST</span>
                <span>Daily summary sent to supervisor channel (open/overdue/waiting parts)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded mt-0.5 flex-shrink-0">Every 2h</span>
                <span>Overdue work order notifications</span>
              </li>
            </ul>
            <p className="text-xs text-muted-foreground mt-3">
              These automations only run when MoltBot is Published. They can be disabled from the configuration panel.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
